import React, { useState, useEffect } from 'react';
import { Plus, Calendar, MapPin, Users, Edit, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { confirmAction } from '../../utils/confirmToast';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import Drawer from '../../components/common/Drawer/Drawer';
import SearchableSelect from '../../components/common/SearchableSelect';
import api from '../../api/axios';
import styles from './Events.module.css';

const TimePicker = ({ value, onChange }) => {
  let h = '09', m = '00', ampm = 'AM';
  if (value && value.includes(':')) {
    const parts = value.split(' ');
    const timeParts = parts[0].split(':');
    h = timeParts[0];
    m = timeParts[1] || '00';
    ampm = parts[1] || 'AM';
  }

  const hours = Array.from({length: 12}, (_, i) => (i + 1).toString().padStart(2, '0'));
  const minutes = ['00', '15', '30', '45'];

  return (
    <div style={{ display: 'flex', gap: '6px', alignItems: 'center', width: '100%' }}>
      <select 
        value={h} 
        onChange={(e) => onChange(`${e.target.value}:${m} ${ampm}`)} 
        style={{ padding: '10px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', flex: 1, backgroundColor: '#fff', fontSize: '14px', cursor: 'pointer' }}
      >
        {hours.map(hr => <option key={hr} value={hr}>{hr}</option>)}
      </select>
      <span style={{ fontWeight: 'bold', color: '#64748b' }}>:</span>
      <select 
        value={m} 
        onChange={(e) => onChange(`${h}:${e.target.value} ${ampm}`)} 
        style={{ padding: '10px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', flex: 1, backgroundColor: '#fff', fontSize: '14px', cursor: 'pointer' }}
      >
        {!minutes.includes(m) && <option value={m}>{m}</option>}
        {minutes.map(min => <option key={min} value={min}>{min}</option>)}
      </select>
      <select 
        value={ampm} 
        onChange={(e) => onChange(`${h}:${m} ${e.target.value}`)} 
        style={{ padding: '10px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', flex: 1, backgroundColor: '#f8fafc', fontSize: '14px', fontWeight: '600', color: '#0f172a', cursor: 'pointer' }}
      >
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
  );
};

const Events = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imageFile, setImageFile] = useState(null);
  const [guestListEvent, setGuestListEvent] = useState(null);

  const baseStandardCategories = [
    'Community', 'Festival', 'Cultural', 'Religious', 'Spiritual', 'Traditional', 
    'Social Gathering', 'Family', 'Friends & Networking', 'Meetup', 'Meeting', 
    'Conference', 'Seminar', 'Workshop', 'Training', 'Webinar', 'Exhibition', 
    'Fair & Mela', 'Business & Corporate', 'Networking', 'Startup & Entrepreneurship', 
    'Career & Jobs', 'Education', 'College & University', 'School', 'Sports', 
    'Fitness', 'Health & Wellness', 'Kids Activities', 'Children', 'Youth', 
    'Women', 'Senior Citizens', 'Music', 'Concert', 'Dance', 'Drama & Theatre', 
    'Art', 'Photography', 'Literature & Poetry', 'Book & Reading', 'Food & Culinary', 
    'Cooking', 'Fashion', 'Beauty & Lifestyle', 'Technology', 'Science', 
    'Innovation', 'Environment', 'Agriculture', 'Charity & Donation', 'Volunteering', 
    'Fundraising', 'Public Awareness', 'Political & Civic', 'Government', 
    'Awards & Recognition', 'Competition', 'Talent Show', 'Quiz', 'Entertainment', 
    'Movie & Screening', 'Travel & Tourism', 'Outdoor Activities', 'Adventure', 
    'Picnic', 'Birthday', 'Anniversary', 'Wedding', 'Engagement', 'Baby Shower', 
    'Family Function', 'Puja & Ceremony', 'Religious Ceremony', 'Inauguration', 
    'Celebration', 'Rally', 'Walkathon', 'Marathon', 'Blood Donation', 'Health Camp', 
    'Job Fair', 'Placement Drive', 'Alumni', 'Matrimonial'
  ];

  const derivedCategories = Array.from(new Set(events.map(e => e.category).filter(Boolean)));
  const customCategoriesList = derivedCategories.filter(c => !baseStandardCategories.includes(c) && c !== 'Other');
  const availableCategories = [...baseStandardCategories, ...customCategoriesList, 'Other'];

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/community/events');
      setEvents(data);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  const [formData, setFormData] = useState({
    title: '', date: '', time: '07:00 PM', location: '', description: '', category: 'Community', customCategory: '', image: ''
  });

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const img = new Image();
      img.onload = () => {
        if (img.width !== 500 || img.height !== 300) {
          toast.error('Image must be exactly 500x300 pixels.');
          e.target.value = '';
          setImageFile(null);
        } else {
          setImageFile(file);
        }
        URL.revokeObjectURL(img.src);
      };
      img.src = URL.createObjectURL(file);
    }
  };

  const openDrawerForAdd = () => {
    setEditingId(null);
    setFormData({ title: '', date: '', time: '07:00 PM', location: '', description: '', category: 'Community', customCategory: '', image: '' });
    setImageFile(null);
    setIsDrawerOpen(true);
  };

  const handleEditClick = (event) => {
    setEditingId(event._id);
    const isStandardCategory = availableCategories.includes(event.category) && event.category !== 'Other';
    
    setFormData({ 
      title: event.title, 
      date: event.date, 
      time: event.time || '07:00 PM', 
      location: event.location, 
      description: event.description,
      category: isStandardCategory ? event.category : 'Other',
      customCategory: isStandardCategory ? '' : event.category,
      image: event.image || ''
    });
    setImageFile(null);
    setIsDrawerOpen(true);
  };

  const handleDelete = async (id) => {
    confirmAction("Are you sure you want to delete this event?", async () => {
      try {
        await api.delete(`/community/events/${id}`);
        setEvents(events.filter(e => e._id !== id));
        toast.success('Event deleted successfully');
      } catch (err) {
        toast.error('Failed to delete event');
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let imageUrl = formData.image;
      
      if (imageFile) {
        const uploadData = new FormData();
        uploadData.append('image', imageFile);
        const uploadRes = await api.post('/upload', uploadData);
        imageUrl = uploadRes.data;
      }

      const finalCategory = formData.category === 'Other' ? formData.customCategory : formData.category;

      const payload = { ...formData, category: finalCategory, image: imageUrl };

      if (editingId) {
        const { data } = await api.put(`/community/events/${editingId}`, payload);
        setEvents(events.map(ev => ev._id === editingId ? data : ev));
        toast.success('Event updated successfully');
      } else {
        const { data } = await api.post('/community/events', payload);
        setEvents([...events, data].sort((a, b) => new Date(a.date) - new Date(b.date)));
        toast.success('Event created successfully');
      }
      setIsDrawerOpen(false);
      setEditingId(null);
      setFormData({ title: '', date: '', time: '', location: '', description: '', category: 'Community', customCategory: '', image: '' });
      setImageFile(null);
    } catch (err) {
      toast.error(editingId ? 'Failed to update event' : 'Failed to create event');
    }
  };

  return (
    <div className={styles.container}>
      <PageHeader 
        title="Society Events" 
        breadcrumb={['Community', 'Events']} 
        actionButton={
          <button className={styles.addBtn} onClick={openDrawerForAdd}>
            <Plus size={16} /> Create Event
          </button>
        }
      />

      <div className={styles.grid}>
        {loading ? <p style={{padding: '0 24px'}}>Loading events...</p> : events.map(event => (
          <div key={event._id} className={styles.card}>
            <div className={styles.imageContainer}>
              <img src={event.image ? (event.image.startsWith('http') ? event.image : `${import.meta.env.VITE_API_URL.replace(/\/api\/?$/, '')}${event.image}`) : 'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?q=80&w=600&auto=format&fit=crop'} alt={event.title} className={styles.eventImage} />
              <div className={styles.actionBtns}>
                <button className={styles.iconBtn} aria-label="Edit Event" onClick={() => handleEditClick(event)}><Edit size={14} /></button>
                <button className={`${styles.iconBtn} ${styles.danger}`} aria-label="Delete Event" onClick={() => handleDelete(event._id)}><Trash2 size={14} /></button>
              </div>
            </div>
            
            <div className={styles.cardBody}>
              <h3>{event.title}</h3>
              <div className={styles.infoRow}>
                <Calendar size={14} />
                <span>{event.date} • {event.time}</span>
              </div>
              <div className={styles.infoRow}>
                <MapPin size={14} />
                <span>{event.location}</span>
              </div>
            </div>
            
            <div className={styles.cardFooter}>
              <div className={styles.attendees}>
                <Users size={14} />
                <span>{(event.attendees ? event.attendees.length : 0) + (event.externalAttendees ? event.externalAttendees.length : 0)} RSVPs</span>
              </div>
              <span className={styles.manageLink} onClick={() => setGuestListEvent(event)} style={{ cursor: 'pointer' }}>View Guest List</span>
            </div>
          </div>
        ))}
      </div>

      <Drawer isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} title={editingId ? "Edit Event" : "Create Event"}>
        <form className="drawerForm" onSubmit={handleSubmit}>
          <div className="formGroup">
            <label>Event Title</label>
            <input name="title" value={formData.title} onChange={handleChange} required placeholder="e.g. Diwali Celebration" />
          </div>
          
          <div className="formGroup">
            <label>Category</label>
            <SearchableSelect name="category" value={formData.category} onChange={handleChange} required id="category-select">
              {availableCategories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </SearchableSelect>
          </div>
          
          {formData.category === 'Other' && (
            <div className="formGroup">
              <label>Custom Category Name</label>
              <input type="text" name="customCategory" value={formData.customCategory} onChange={handleChange} required placeholder="Enter custom category" />
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="formGroup">
              <label>Date</label>
              <input type="date" name="date" value={formData.date} onChange={handleChange} required />
            </div>
            <div className="formGroup">
              <label>Time</label>
              <TimePicker value={formData.time} onChange={(val) => setFormData({...formData, time: val})} />
            </div>
          </div>
          <div className="formGroup">
            <label>Location</label>
            <input name="location" value={formData.location} onChange={handleChange} required placeholder="e.g. Clubhouse" />
          </div>
          <div className="formGroup">
            <label>Description</label>
            <textarea name="description" value={formData.description} onChange={handleChange} rows="4" placeholder="Event details..."></textarea>
          </div>
          
          <div className="formGroup">
            <label>
              Event Image (Required size: 500x300)
              <span style={{ display: 'block', fontSize: '12px', color: '#ef4444', marginTop: '4px', fontWeight: 'normal' }}>Image MUST be exactly 500x300 pixels</span>
            </label>
            <input type="file" accept="image/*" onChange={handleImageChange} />
          </div>
          
          <div className="formActions">
            <button type="button" className="btnCancel" onClick={() => setIsDrawerOpen(false)}>Cancel</button>
            <button type="submit" className="btnSubmit">{editingId ? "Update Event" : "Create Event"}</button>
          </div>
        </form>
      </Drawer>

      {/* Guest List Drawer */}
      <Drawer isOpen={!!guestListEvent} onClose={() => setGuestListEvent(null)} title={`Guest List - ${guestListEvent?.title}`}>
        <div style={{ padding: '20px 0' }}>
          {(guestListEvent?.attendees?.length > 0 || guestListEvent?.externalAttendees?.length > 0) ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              
              {/* Resident RSVPs */}
              {guestListEvent?.attendees?.map(attendee => (
                <div key={attendee._id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: '#f9fafb', border: '1px solid #ebf0ec', borderRadius: '12px' }}>
                  <img 
                    src={attendee.profileImage ? (attendee.profileImage.startsWith('http') ? attendee.profileImage : `${import.meta.env.VITE_API_URL.replace(/\/api\/?$/, '')}${attendee.profileImage}`) : 'https://cdn-icons-png.flaticon.com/512/3135/3135715.png'} 
                    alt={attendee.fullName} 
                    style={{ width: '48px', height: '48px', borderRadius: '50%', objectFit: 'cover' }} 
                  />
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '15px', color: '#111827' }}>{attendee.fullName}</div>
                    <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '2px' }}>
                      Flat: {attendee.flatNumber} ({attendee.wing})
                      <span style={{marginLeft: '8px', padding: '2px 6px', background: '#e0e7ff', color: '#4338ca', borderRadius: '4px', fontSize: '10px'}}>Resident App</span>
                    </div>
                  </div>
                </div>
              ))}

              {/* Public Form RSVPs */}
              {guestListEvent?.externalAttendees?.map((ext, idx) => (
                <div key={`ext-${idx}`} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '12px', background: '#fefce8', border: '1px solid #fef08a', borderRadius: '12px' }}>
                  <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: '#fde047', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#854d0e', fontWeight: 'bold', fontSize: '18px', flexShrink: 0 }}>
                    {ext.fullName.charAt(0).toUpperCase()}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ fontWeight: '600', fontSize: '15px', color: '#111827' }}>{ext.fullName}</div>
                      {ext.rsvp && (
                        <span style={{
                          padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600',
                          backgroundColor: ext.rsvp === 'Yes' ? '#d1fae5' : ext.rsvp === 'Maybe' ? '#fef3c7' : '#ffe4e6',
                          color: ext.rsvp === 'Yes' ? '#059669' : ext.rsvp === 'Maybe' ? '#d97706' : '#e11d48'
                        }}>
                          {ext.rsvp}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '2px' }}>
                      Flat: {ext.flatNumber} • +{Math.max(0, ext.attendeesCount - 1)} Guests
                      <span style={{marginLeft: '8px', padding: '2px 6px', background: '#fef08a', color: '#854d0e', borderRadius: '4px', fontSize: '10px'}}>Public Form</span>
                    </div>
                    {ext.phone && (
                      <div style={{ fontSize: '12px', color: '#4b5563', marginTop: '4px' }}>
                        📞 {ext.phone} {ext.email && ext.email !== 'N/A' && `| ✉️ ${ext.email}`}
                      </div>
                    )}
                    {ext.notes && (
                      <div style={{ fontSize: '12px', color: '#4b5563', marginTop: '6px', padding: '6px 8px', background: '#fffbeb', borderRadius: '6px', border: '1px solid #fef08a', whiteSpace: 'pre-wrap' }}>
                        {ext.notes}
                      </div>
                    )}
                  </div>
                </div>
              ))}

            </div>
          ) : (
            <div style={{ textAlign: 'center', color: '#6b7280', padding: '40px 0', background: '#f9fafb', borderRadius: '12px', border: '1px dashed #d1d5db' }}>
              <p style={{ margin: 0 }}>No RSVPs yet.</p>
            </div>
          )}
        </div>
      </Drawer>
    </div>
  );
};

export default Events;
